import { isApiError } from "@core/api/api-error";
import { getLogger } from "@core/logging/logger";
import { AuthService } from "@domain/authentication/auth-service";
import { UsernameExistsCodeError } from "@domain/authentication/authentication-errors";
import { InfraManager } from "@domain/infra/infra-manager";
import { CreateVipPtInput } from "@domain/register/register-vip/vip-register";
import { VipRegisterService } from "@domain/register/register-vip/vip-register-service";
import { InvalidVeevaUser, VeevaErrorCodes } from "@domain/register/register-vip/vip-user-data-error";
import { VeevaUserRecordData } from "@domain/veeva/veeva";
import { VeevaService } from "@domain/veeva/veeva-service";

export class VipRegisterManager {
	private readonly vipUserLogger = getLogger("🌟️ [VipRegisterManager]");

	constructor(
		private readonly vipRegisterService: VipRegisterService,
		private readonly veevaService: VeevaService,
		private readonly authService: AuthService,
		private readonly infraManager: InfraManager,
	) {}

	async getVeevaUserData(veevaId: string): Promise<{ record: VeevaUserRecordData; emailAlreadyExists: boolean }> {
		try {
			this.vipUserLogger.info(`Try to get Veeva user data with id : ${veevaId}`);
			const veevaUserRecordData = await this.veevaService.getVeevaUserData(veevaId);
			const email = veevaUserRecordData.email;
			let emailAlreadyExists = false;
			if (email) {
				emailAlreadyExists = !(await this.authService.isEmailAvailable(email));
				if (emailAlreadyExists) {
					this.vipUserLogger.info("Veeva Email already exists. Redirect to login...");
				}
			}

			await this.infraManager.changeInfrastructureUsingCountryCode(veevaUserRecordData.countryCode);

			return { record: veevaUserRecordData, emailAlreadyExists };
		} catch (error) {
			if (isApiError(error) && error.code === VeevaErrorCodes.InvalidVeevaUser) {
				this.vipUserLogger.error("Invalid Veeva user (wrong id,  missing information, ...)");
				throw new InvalidVeevaUser();
			}
			this.vipUserLogger.error("An error occurred: ", error);
			throw error;
		}
	}

	async registerVipUser(veevaPtInput: CreateVipPtInput): Promise<void> {
		try {
			const emailAlreadyExists = !(await this.authService.isEmailAvailable(veevaPtInput.email));
			if (emailAlreadyExists) {
				throw new UsernameExistsCodeError();
			}
			await this.vipRegisterService.registerVipUser(veevaPtInput);
		} catch (e) {
			if ((isApiError(e) && e.code === VeevaErrorCodes.PtEmailAlreadyExists) || e instanceof UsernameExistsCodeError) {
				throw new UsernameExistsCodeError();
			}
			throw e;
		}
	}
}
